/**
 * Shared form primitives and small visual components used across the
 * Fee Calculator views (Calculator, Proposal, SettingsPanel). Ported
 * unchanged from the source artifact so the visual identity is
 * preserved exactly.
 */

import React from "react";
import { C } from "./brand";
import { fmtZAR } from "./feeEngine";

export const inputBase = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 15,
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  background: C.white,
  color: C.text,
  fontFamily: "inherit",
  outline: "none",
};

export function Field({ label, hint, children }) {
  return (
    <div className="mb-4">
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.teal, marginBottom: 6, letterSpacing: 0.3, textTransform: "uppercase" }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export function NumberInput({ value, onChange, prefix, suffix, placeholder, min, step, format }) {
  const isCurrency = format === "currency";

  // For currency: display formatted with thousand separators, no cents.
  // Internally we still store a Number; the input is type="text" because
  // type="number" cannot display formatted strings.
  const displayValue = isCurrency
    ? value === null || value === undefined || value === ""
      ? ""
      : Number(value).toLocaleString("en-ZA", { maximumFractionDigits: 0 })
    : value ?? "";

  const handleChange = (e) => {
    if (isCurrency) {
      const digits = e.target.value.replace(/[^\d]/g, "");
      onChange(digits === "" ? null : Number(digits));
    } else {
      const v = e.target.value;
      onChange(v === "" ? null : Number(v));
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {prefix && (
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 14, pointerEvents: "none" }}>
          {prefix}
        </span>
      )}
      <input
        type={isCurrency ? "text" : "number"}
        inputMode={isCurrency ? "numeric" : undefined}
        value={displayValue}
        min={isCurrency ? undefined : min}
        step={isCurrency ? undefined : step}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={(e) => (e.target.style.borderColor = C.tealMid)}
        onBlur={(e) => (e.target.style.borderColor = C.border)}
        style={{ ...inputBase, paddingLeft: prefix ? 32 : 12, paddingRight: suffix ? 36 : 12 }}
      />
      {suffix && (
        <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 14, pointerEvents: "none" }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

export function RadioGroup({ value, onChange, options }) {
  return (
    <div className="flex flex-col gap-2">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <label
            key={o.value}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              border: `1px solid ${active ? C.tealMid : C.border}`,
              background: active ? "#F2F7F8" : C.white,
              borderRadius: 4,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            <input
              type="radio"
              checked={active}
              onChange={() => onChange(o.value)}
              style={{ accentColor: C.tealDark, cursor: "pointer" }}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{o.label}</div>
              {o.hint && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{o.hint}</div>}
            </div>
          </label>
        );
      })}
    </div>
  );
}

export function CheckboxToggle({ checked, onChange, label, hint }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 12px",
        border: `1px solid ${checked ? C.tealMid : C.border}`,
        background: checked ? "#F2F7F8" : C.white,
        borderRadius: 4,
        cursor: "pointer",
        transition: "all 0.15s",
        marginBottom: 8,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: C.tealDark, cursor: "pointer", marginTop: 2 }}
      />
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>{hint}</div>}
      </div>
    </label>
  );
}

export function SubLabel({ children }) {
  return (
    <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 500 }}>{children}</div>
  );
}

export function ResultCard({ title, accent, children }) {
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${accent ? C.tealMid : C.border}`,
        borderLeft: `4px solid ${accent ? C.tealDark : C.border}`,
        borderRadius: 4,
        padding: 20,
        marginBottom: 16,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: C.teal, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 16 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

export function KV({ label, value, bold, highlight }) {
  return (
    <div className="flex justify-between items-baseline" style={{ padding: "6px 0" }}>
      <span style={{ fontSize: 13, color: C.muted }}>{label}</span>
      <span
        style={{
          fontSize: highlight ? 17 : 14,
          color: highlight ? C.tealDark : C.text,
          fontWeight: bold || highlight ? 600 : 400,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function Divider() {
  return <div style={{ height: 1, background: C.border, margin: "8px 0" }} />;
}

export function Empty({ msg }) {
  return <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, padding: "8px 0" }}>{msg}</div>;
}

export function CapWarning({ target, actual, capPct }) {
  return (
    <div
      style={{
        marginTop: 12,
        padding: 10,
        background: "#FEF4EE",
        border: `1px solid ${C.orange}`,
        borderRadius: 4,
        fontSize: 12,
        color: C.text,
        lineHeight: 1.5,
      }}
    >
      <strong style={{ color: C.orange }}>⚠ Cap applied.</strong> Hourly back-solve exceeded the {capPct} ceiling.
      Earnings reduced from <strong>{fmtZAR(target)}</strong> to <strong>{fmtZAR(actual)}</strong>.
    </div>
  );
}
